import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Footer from './Footer';
import SplitTextTest from './SplitTextTest';

const AboutUsPage = () => {
  const containerRef = useRef(null);
  const [footerData, setFooterData] = useState(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  useEffect(() => {
    // Fetch footer data
    fetch('/data/sample.json')
      .then(res => res.json())
      .then(data => setFooterData(data.footer))
      .catch(err => console.error('Error loading footer data:', err));
  }, []);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Scroll-based animations
  const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  const heroY = useTransform(scrollYProgress, [0, 0.3], [100, 0]);
  
  const sectionOpacity = useTransform(scrollYProgress, [0.2, 0.4, 0.8, 1], [0, 1, 1, 0]);
  const sectionY = useTransform(scrollYProgress, [0.2, 0.4, 0.8, 1], [50, 0, 0, -50]);

  const sections = [
    { id: 1, title: "About Us", subtitle: "Learn more about our story" },
    { id: 2, title: "Who We Are", subtitle: "Meet the team behind the innovation" },
    { id: 3, title: "What We Do", subtitle: "Discover our services and solutions" },
    { id: 4, title: "Our Mission", subtitle: "Our purpose and commitment" },
    { id: 5, title: "Our Vision", subtitle: "Where we're heading" },
    { id: 6, title: "Why Choose Us", subtitle: "What sets us apart" },
    { id: 7, title: "Our Promise", subtitle: "Our commitment to you" }
  ];

  return (
    <div ref={containerRef} className="min-h-screen w-full about-us-page" style={{ backgroundColor: 'oklch(0.23 0.002 264)' }}>
      {/* Hero Section */}
      <section className="relative h-screen w-full flex flex-col items-center justify-center overflow-hidden" style={{ backgroundColor: 'oklch(0.23 0.002 264)' }}>
        {/* Animated Background Mesh Graphics */}
        <div className="absolute inset-0 overflow-hidden opacity-30">
          {/* Left Top Mesh */}
          <motion.div
            animate={{
              rotate: [0, 360],
              scale: [1, 1.2, 1],
            }}
            transition={{
              rotate: { duration: 30, repeat: Infinity, ease: "linear" },
              scale: { duration: 8, repeat: Infinity, ease: "easeInOut" }
            }}
            className="absolute top-0 left-0 w-64 h-64 lg:w-80 lg:h-80"
          >
            <svg width="100%" height="100%" viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <linearGradient id="meshGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
                  <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.6" />
                </linearGradient>
              </defs>
              {/* Mesh Network */}
              {Array.from({ length: 8 }).map((_, i) => {
                const angle = (i * 45) * (Math.PI / 180);
                const radius = 70;
                const x = 100 + radius * Math.cos(angle);
                const y = 100 + radius * Math.sin(angle);
                return (
                  <g key={i}>
                    <circle cx={x} cy={y} r="3" fill="url(#meshGrad1)">
                      <animate attributeName="r" values="3;5;3" dur={`${2 + i * 0.2}s`} repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.4;0.9;0.4" dur={`${2 + i * 0.2}s`} repeatCount="indefinite" />
                    </circle>
                    <line x1="100" y1="100" x2={x} y2={y} stroke="url(#meshGrad1)" strokeWidth="1" opacity="0.3">
                      <animate attributeName="opacity" values="0.2;0.6;0.2" dur={`${3 + i * 0.3}s`} repeatCount="indefinite" />
                    </line>
                  </g>
                );
              })}
              <circle cx="100" cy="100" r="8" fill="url(#meshGrad1)">
                <animate attributeName="r" values="8;12;8" dur="2s" repeatCount="indefinite" />
              </circle>
            </svg>
          </motion.div>

          {/* Right Bottom Mesh */}
          <motion.div
            animate={{
              rotate: [360, 0],
              scale: [1, 1.15, 1],
            }}
            transition={{
              rotate: { duration: 40, repeat: Infinity, ease: "linear" },
              scale: { duration: 10, repeat: Infinity, ease: "easeInOut" }
            }}
            className="absolute bottom-0 right-0 w-64 h-64 lg:w-80 lg:h-80"
          >
            <svg width="100%" height="100%" viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <linearGradient id="meshGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.6" />
                  <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.6" />
                </linearGradient>
              </defs>
              {/* Hexagonal Pattern */}
              <polygon points="100,30 130,50 130,80 100,100 70,80 70,50" fill="none" stroke="url(#meshGrad2)" strokeWidth="1.5" opacity="0.4">
                <animate attributeName="opacity" values="0.3;0.7;0.3" dur="3s" repeatCount="indefinite" />
              </polygon>
              <polygon points="100,70 120,85 120,105 100,120 80,105 80,85" fill="none" stroke="url(#meshGrad2)" strokeWidth="1.5" opacity="0.5">
                <animate attributeName="opacity" values="0.4;0.8;0.4" dur="2.5s" repeatCount="indefinite" />
              </polygon>
              <circle cx="100" cy="100" r="6" fill="url(#meshGrad2)">
                <animate attributeName="r" values="6;10;6" dur="2s" repeatCount="indefinite" />
              </circle>
            </svg>
          </motion.div>
        </div>

        {/* Central Content */}
        <div className="relative z-10 mt-10 max-w-7xl mx-auto px-8 text-center py-8 lg:py-10">
          {/* Main Motion Graphic - Large and Prominent */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="mb-4 lg:mb-6"
          >
            <div className="w-32 h-32 lg:w-40 lg:h-40 mx-auto">
              <svg width="100%" height="100%" viewBox="0 0 300 300" className="w-full h-full">
                <defs>
                  <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="50%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                  <radialGradient id="glowGradient" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.8" />
                    <stop offset="70%" stopColor="#8b5cf6" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.1" />
                  </radialGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>

                {/* Outer Glowing Rings */}
                <circle cx="150" cy="150" r="120" fill="none" stroke="url(#heroGradient)" strokeWidth="2" opacity="0.3" filter="url(#glow)">
                  <animate attributeName="r" values="120;135;120" dur="4s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.2;0.5;0.2" dur="4s" repeatCount="indefinite" />
                </circle>
                <circle cx="150" cy="150" r="100" fill="none" stroke="url(#heroGradient)" strokeWidth="2" opacity="0.4" filter="url(#glow)">
                  <animate attributeName="r" values="100;115;100" dur="3.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.3;0.6;0.3" dur="3.5s" repeatCount="indefinite" />
                </circle>
                <circle cx="150" cy="150" r="80" fill="none" stroke="url(#heroGradient)" strokeWidth="2" opacity="0.5" filter="url(#glow)">
                  <animate attributeName="r" values="80;95;80" dur="3s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.4;0.7;0.4" dur="3s" repeatCount="indefinite" />
                </circle>

                {/* Central Core with Glow */}
                <circle cx="150" cy="150" r="50" fill="url(#glowGradient)" opacity="0.6" filter="url(#glow)">
                  <animate attributeName="r" values="50;60;50" dur="2.5s" repeatCount="indefinite" />
                </circle>
                <circle cx="150" cy="150" r="30" fill="url(#heroGradient)" filter="url(#glow)">
                  <animate attributeName="r" values="30;35;30" dur="2s" repeatCount="indefinite" />
                </circle>
                <circle cx="150" cy="150" r="12" fill="#ffffff" opacity="0.9">
                  <animate attributeName="r" values="12;15;12" dur="1.5s" repeatCount="indefinite" />
                </circle>

                {/* Rotating Network Nodes */}
                {Array.from({ length: 12 }).map((_, i) => {
                  const angle = (i * 30) * (Math.PI / 180);
                  const radius = 90;
                  const x = 150 + radius * Math.cos(angle);
                  const y = 150 + radius * Math.sin(angle);
                  return (
                    <g key={i}>
                      <line x1="150" y1="150" x2={x} y2={y} stroke="url(#heroGradient)" strokeWidth="1.5" opacity="0.4">
                        <animateTransform
                          attributeName="transform"
                          attributeType="XML"
                          type="rotate"
                          values={`0 150 150;360 150 150`}
                          dur={`${15 + i * 2}s`}
                          repeatCount="indefinite"
                        />
                        <animate attributeName="opacity" values="0.3;0.7;0.3" dur={`${2 + i * 0.2}s`} repeatCount="indefinite" />
                      </line>
                      <circle cx={x} cy={y} r="4" fill="url(#heroGradient)">
                        <animateTransform
                          attributeName="transform"
                          attributeType="XML"
                          type="rotate"
                          values={`0 150 150;360 150 150`}
                          dur={`${15 + i * 2}s`}
                          repeatCount="indefinite"
                        />
                        <animate attributeName="r" values="4;6;4" dur={`${1.5 + i * 0.1}s`} repeatCount="indefinite" />
                      </circle>
                    </g>
                  );
                })}

                {/* Floating Particles */}
                {Array.from({ length: 8 }).map((_, i) => {
                  const angle = (i * 45) * (Math.PI / 180);
                  const radius = 65 + (i % 3) * 15;
                  const x = 150 + radius * Math.cos(angle);
                  const y = 150 + radius * Math.sin(angle);
                  return (
                    <circle key={`particle-${i}`} cx={x} cy={y} r="2" fill="#06b6d4" opacity="0.7">
                      <animateTransform
                        attributeName="transform"
                        attributeType="XML"
                        type="rotate"
                        values={`0 150 150;360 150 150`}
                        dur={`${8 + i * 0.5}s`}
                        repeatCount="indefinite"
                      />
                      <animate attributeName="opacity" values="0.5;1;0.5" dur={`${2.5 + i * 0.3}s`} repeatCount="indefinite" />
                    </circle>
                  );
                })}
              </svg>
            </div>
          </motion.div>

          {/* Title with Gradient */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="text-3xl lg:text-5xl xl:text-6xl font-bold mb-3 lg:mb-4 font-slim leading-tight"
          >
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Building Great
            </span>
            <br />
            <span className="bg-gradient-to-r from-purple-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
              AI Solutions
            </span>
            <br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              Since Day One!
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: "easeOut" }}
            className="text-base lg:text-lg xl:text-xl text-gray-300 max-w-3xl mx-auto font-slim leading-relaxed mb-2"
          >
            To make automation accessible, powerful, and tailored
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: "easeOut" }}
            className="text-sm lg:text-base xl:text-lg text-gray-400 max-w-2xl mx-auto font-slim leading-relaxed"
          >
            Helping businesses focus on what truly matters: growth, creativity, and impact.
          </motion.p>
        </div>

      </section>

      {/* Our History Section */}
      <section className="py-20 px-8 w-full bg-gray-50" data-section="our-history">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8 font-slim">
              Our History
            </h2>
            <div className="space-y-6 text-lg text-gray-600 font-slim leading-relaxed">
              <p>
              FlowMatic began with a simple belief — that automation should be smart, simple, and impactful. Founded by a team of passionate technologists and strategists, we started by building small AI systems for local businesses.
              </p>
              <p>
              As our innovations gained traction, we expanded into full-scale automation solutions — from AI receptionists and lead generation bots to social media and client management systems.
              </p>
              <p>
              Today, FlowMatic stands as a growing automation agency dedicated to empowering global businesses with tools that think, learn, and evolve.
              </p>
              <p>
                We use the latest technology, individual potential, and team collaboration to deliver the best results for our clients.
              </p>
              <p className="font-semibold text-gray-900">
                We're not here to reinvent the wheel but to set the bar higher
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
              viewport={{ once: true }}
              className="mt-12"
            >
              <div className="text-4xl lg:text-6xl font-bold text-gray-900 font-slim">
                <SplitTextTest 
                  text="Join Our Mission"
                  className=""
                  delay={0.1}
                  duration={0.6}
                  ease="power3.out"
                  from={{ opacity: 0, y: 40 }}
                  to={{ opacity: 1, y: 0 }}
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 px-8 w-full">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4 font-slim">
              OUR VALUES
            </h2>
            <h3 className="text-2xl lg:text-3xl font-semibold text-white mb-8 font-slim">
              What Makes FlowMatic™, FlowMatic™
            </h3>
            <p className="text-lg text-white font-slim">
              These are the main values that shape who we are.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Trust > Money",
                description: "Building trust takes time and effort, but it can be lost in an instant. It's priceless to be able to trust the people you work with every day."
              },
              {
                title: "Win; Help Win",
                description: "We celebrate each other's victories and support one another's growth. When one of us wins, we all win."
              },
              {
                title: "Get Sh*t Done",
                description: "At FlowMatic™, we're all about GSD (Getting Sh*t Done) - efficient, effective, and together. No excuses, just results."
              },
              {
                title: "Customer Obsession",
                description: "Our clients are at the heart of everything we do. We're committed to delivering results and solutions."
              },
              {
                title: "This is FLOWMATIC™!!",
                description: "Excellence is in our DNA. We've created a world where top performers team up to do great work that makes us proud."
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                 className="relative rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-shadow duration-300 group"
                style={{ 
                  backgroundColor: 'oklch(0.23 0.002 264)',
                  border: '2px solid transparent',
                  backgroundImage: 'linear-gradient(oklch(0.23 0.002 264), oklch(0.23 0.002 264)), linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box'
                }}
              >
                <h4 className="text-xl font-bold text-white mb-4 font-slim">
                  {value.title}
                </h4>
                <p className="text-gray-300 font-slim leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="relative py-20 px-8 w-full bg-white" data-section="why-choose-us">
        <div className="max-w-6xl mx-auto">
          {/* Fixed Title */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="text-center mb-16 lg:mb-24"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 font-slim">
              WHY CHOOSE US
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 font-slim">
              The factors that make us the best choice for your automation needs
            </p>
          </motion.div>

          {/* Scroll Timeline */}
          <div className="relative">
            {/* Central Vertical Line */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-blue-500"></div>

            {[
              {
                title: "Tailored Solutions",
                description: "Every business is unique — so are our automations.",
                icon: "🎯"
              },
              {
                title: "Cutting-Edge Technology",
                description: "We stay ahead so your business can too.",
                icon: "⚡"
              },
              {
                title: "Dedicated Support",
                description: "From discovery to implementation, we're with you every step.",
                icon: "🤝"
              },
              {
                title: "Proven Results",
                description: "Automation that doesn't just impress — it performs.",
                icon: "📈"
              }
            ].map((item, index) => {
              const isEven = index % 2 === 0;
              
              return (
                <div key={index} className="relative mb-24 lg:mb-40">
                  <motion.div
                    initial={{ opacity: 0, x: isEven ? -100 : 100 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                    viewport={{ once: true, margin: "-100px" }}
                    className={`relative ${
                      isEven ? 'lg:w-1/2 lg:pr-8' : 'lg:w-1/2 lg:ml-auto lg:pl-8'
                    }`}
                  >
                    {/* Card */}
                    <div className="rounded-xl p-8 lg:p-10 shadow-lg hover:shadow-xl transition-all duration-300" style={{ backgroundColor: 'oklch(0.23 0.002 264)' }}>
                      <div className="flex flex-col items-center text-center">
                        {/* Motion Graphic Icon */}
                        <div className="mb-6 w-24 h-24">
                          <svg width="96" height="96" viewBox="0 0 100 100" className="w-full h-full">
                            {index === 0 && (
                              <>
                                {/* Central Hub Pattern */}
                                <circle cx="50" cy="50" r="35" fill="none" stroke="url(#grad1)" strokeWidth="1.5" opacity="0.3">
                                  <animate attributeName="r" values="35;40;35" dur="3s" repeatCount="indefinite" />
                                </circle>
                                <circle cx="50" cy="50" r="25" fill="none" stroke="url(#grad1)" strokeWidth="1.5" opacity="0.4">
                                  <animate attributeName="r" values="25;30;25" dur="2.5s" repeatCount="indefinite" />
                                </circle>
                                <circle cx="50" cy="50" r="15" fill="none" stroke="url(#grad1)" strokeWidth="2" opacity="0.6">
                                  <animate attributeName="r" values="15;20;15" dur="2s" repeatCount="indefinite" />
                                </circle>
                                <circle cx="50" cy="50" r="5" fill="url(#grad1)" />
                                <defs>
                                  <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#8b5cf6" />
                                  </linearGradient>
                                </defs>
                              </>
                            )}
                            {index === 1 && (
                              <>
                                {/* Network Mesh Pattern */}
                                <line x1="50" y1="10" x2="50" y2="90" stroke="url(#grad2)" strokeWidth="1.5" opacity="0.6">
                                  <animate attributeName="opacity" values="0.3;0.8;0.3" dur="2s" repeatCount="indefinite" />
                                </line>
                                <line x1="10" y1="50" x2="90" y2="50" stroke="url(#grad2)" strokeWidth="1.5" opacity="0.6">
                                  <animate attributeName="opacity" values="0.3;0.8;0.3" dur="2.5s" repeatCount="indefinite" />
                                </line>
                                <line x1="22.5" y1="22.5" x2="77.5" y2="77.5" stroke="url(#grad2)" strokeWidth="1.5" opacity="0.5">
                                  <animate attributeName="opacity" values="0.2;0.7;0.2" dur="2.2s" repeatCount="indefinite" />
                                </line>
                                <line x1="77.5" y1="22.5" x2="22.5" y2="77.5" stroke="url(#grad2)" strokeWidth="1.5" opacity="0.5">
                                  <animate attributeName="opacity" values="0.2;0.7;0.2" dur="2.7s" repeatCount="indefinite" />
                                </line>
                                <circle cx="50" cy="50" r="8" fill="url(#grad2)" />
                                <defs>
                                  <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#8b5cf6" />
                                  </linearGradient>
                                </defs>
                              </>
                            )}
                            {index === 2 && (
                              <>
                                {/* Flow Style Pattern */}
                                <path d="M 20 50 Q 35 20, 50 50 T 80 50" stroke="url(#grad3)" strokeWidth="2" fill="none" strokeLinecap="round">
                                  <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite" />
                                </path>
                                <path d="M 20 50 Q 35 80, 50 50 T 80 50" stroke="url(#grad3)" strokeWidth="2" fill="none" strokeLinecap="round">
                                  <animate attributeName="opacity" values="0.3;1;0.3" dur="2.3s" repeatCount="indefinite" />
                                </path>
                                <circle cx="50" cy="50" r="4" fill="url(#grad3)" />
                                <circle cx="30" cy="35" r="2" fill="url(#grad3)" opacity="0.7">
                                  <animate attributeName="opacity" values="0.7;1;0.7" dur="1.8s" repeatCount="indefinite" />
                                </circle>
                                <circle cx="70" cy="45" r="2" fill="url(#grad3)" opacity="0.7">
                                  <animate attributeName="opacity" values="0.7;1;0.7" dur="2.2s" repeatCount="indefinite" />
                                </circle>
                                <defs>
                                  <linearGradient id="grad3" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#8b5cf6" />
                                  </linearGradient>
                                </defs>
                              </>
                            )}
                            {index === 3 && (
                              <>
                                {/* Radial Pattern */}
                                <path d="M 50 50 L 50 10 M 50 50 L 80 30 M 50 50 L 90 50 M 50 50 L 80 70 M 50 50 L 50 90 M 50 50 L 20 70 M 50 50 L 10 50 M 50 50 L 20 30" 
                                      stroke="url(#grad4)" strokeWidth="2" strokeLinecap="round" opacity="0.6">
                                  <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="8s" repeatCount="indefinite" />
                                </path>
                                <circle cx="50" cy="50" r="8" fill="url(#grad4)" />
                                <defs>
                                  <linearGradient id="grad4" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#3b82f6" />
                                    <stop offset="100%" stopColor="#8b5cf6" />
                                  </linearGradient>
                                </defs>
                              </>
                            )}
                          </svg>
                        </div>
                        <h3 className="text-xl lg:text-2xl font-bold text-white font-slim mb-3">
                          {item.title}
                        </h3>
                        <p className="text-base text-gray-300 font-slim leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Circle Badge - Positioned on timeline */}
                  <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 shadow-lg hover:scale-110 transition-transform duration-300 z-10"></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer Section */}
      {footerData && <Footer footerData={footerData} />}
    </div>
  );
};

export default AboutUsPage;
